import React, { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Upload, Download, FileSpreadsheet, CheckCircle, XCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { ALL_SYSTEM_TYPES, getSystemTypeLabel } from '@/lib/systemTypeConstants';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

interface CodigoRow {
  codigo: string;
  tipo_sistema: string;
  descripcion: string;
  severidad: string;
  solucion_sugerida?: string;
}

interface ParsedRow {
  data: CodigoRow;
  errors: string[];
  rowIndex: number;
}

type ImportStep = 'upload' | 'preview' | 'importing' | 'done';

const TEMPLATE_COLUMNS = ['codigo', 'tipo_sistema', 'descripcion', 'severidad', 'solucion_sugerida'];

const COLUMN_LABELS: Record<string, string> = {
  codigo: 'Código *',
  tipo_sistema: 'Tipo Sistema *',
  descripcion: 'Descripción *',
  severidad: 'Severidad *',
  solucion_sugerida: 'Solución Sugerida',
};

const VALID_SEVERITIES = ['baja', 'media', 'alta'];
const BATCH_SIZE = 50;

export const ImportarCodigosAnomaliaDialog: React.FC<Props> = ({ open, onOpenChange, onSuccess }) => {
  const { perfil } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<ImportStep>('upload');
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [progress, setProgress] = useState(0);
  const [importResult, setImportResult] = useState({ created: 0, errors: 0 });

  const reset = useCallback(() => {
    setStep('upload');
    setParsedRows([]);
    setProgress(0);
    setImportResult({ created: 0, errors: 0 });
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleOpenChange = (value: boolean) => {
    if (!value) reset();
    onOpenChange(value);
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      TEMPLATE_COLUMNS.map(c => COLUMN_LABELS[c] || c),
      ['AL001', 'intrusion', 'Fallo de comunicación con CRA', 'alta', 'Verificar línea telefónica y reiniciar central'],
      ['AL002', 'cctv', 'Cámara sin señal', 'media', 'Comprobar alimentación y cableado'],
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Códigos Anomalías');
    ws['!cols'] = TEMPLATE_COLUMNS.map(() => ({ wch: 25 }));
    XLSX.writeFile(wb, 'plantilla_codigos_anomalias.xlsx');
  };

  const parseFile = async (file: File) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: '' });

        if (jsonData.length === 0) {
          toast({ title: 'Archivo vacío', description: 'El archivo no contiene datos.', variant: 'destructive' });
          return;
        }

        // Load existing codes to detect duplicates
        const { data: existingCodes } = await supabase.from('codigos_anomalias').select('codigo');
        const existingSet = new Set((existingCodes || []).map(c => c.codigo.toLowerCase().trim()));

        const validTypes = new Set(Object.keys(ALL_SYSTEM_TYPES).map(t => t.toLowerCase()));

        const reverseMap: Record<string, string> = {};
        for (const key of TEMPLATE_COLUMNS) {
          reverseMap[(COLUMN_LABELS[key] || key).toLowerCase().replace(/\s*\*\s*/g, '').trim()] = key;
          reverseMap[key.toLowerCase()] = key;
        }

        const seenCodes = new Set<string>();
        const rows: ParsedRow[] = jsonData.map((row, idx) => {
          const mapped: Record<string, string> = {};
          for (const [header, value] of Object.entries(row)) {
            const normalizedHeader = header.toLowerCase().replace(/\s*\*\s*/g, '').trim();
            const matchedKey = reverseMap[normalizedHeader];
            if (matchedKey) mapped[matchedKey] = String(value || '').trim();
          }

          const errors: string[] = [];
          if (!mapped.codigo) errors.push('Falta código');
          else if (existingSet.has(mapped.codigo.toLowerCase().trim())) errors.push('Código ya existe');
          else if (seenCodes.has(mapped.codigo.toLowerCase().trim())) errors.push('Código duplicado en archivo');
          else seenCodes.add(mapped.codigo.toLowerCase().trim());

          if (!mapped.tipo_sistema) errors.push('Falta tipo sistema');
          else if (!validTypes.has(mapped.tipo_sistema.toLowerCase().trim())) errors.push('Tipo sistema inválido');

          if (!mapped.descripcion) errors.push('Falta descripción');

          if (!mapped.severidad) errors.push('Falta severidad');
          else if (!VALID_SEVERITIES.includes(mapped.severidad.toLowerCase().trim())) errors.push('Severidad inválida (baja/media/alta)');

          return { data: mapped as unknown as CodigoRow, errors, rowIndex: idx + 2 };
        });

        setParsedRows(rows);
        setStep('preview');
      } catch {
        toast({ title: 'Error al leer archivo', description: 'No se pudo parsear el archivo.', variant: 'destructive' });
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) parseFile(file);
  };

  const validRows = parsedRows.filter(r => r.errors.length === 0);
  const invalidRows = parsedRows.filter(r => r.errors.length > 0);

  const handleImport = async () => {
    if (!perfil?.empresa_id) return;
    setStep('importing');
    let created = 0;
    let errors = 0;
    const totalOps = validRows.length;

    const inserts = validRows.map(r => ({
      codigo: r.data.codigo,
      tipo_sistema: r.data.tipo_sistema.toLowerCase().trim(),
      descripcion: r.data.descripcion,
      severidad: r.data.severidad.toLowerCase().trim(),
      solucion_sugerida: r.data.solucion_sugerida || null,
      empresa_id: perfil.empresa_id,
    }));

    for (let i = 0; i < inserts.length; i += BATCH_SIZE) {
      const batch = inserts.slice(i, i + BATCH_SIZE);
      const { error } = await supabase.from('codigos_anomalias').insert(batch as any);
      if (error) { errors += batch.length; } else { created += batch.length; }
      setProgress(Math.round(((created + errors) / totalOps) * 100));
    }

    setImportResult({ created, errors: errors + invalidRows.length });
    setStep('done');
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[750px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Importar Códigos de Anomalías
          </DialogTitle>
          <DialogDescription>
            Importa códigos de anomalías desde un archivo Excel o CSV para tu empresa.
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-6 py-4">
            <div className="flex justify-center">
              <Button variant="outline" onClick={downloadTemplate} className="gap-2">
                <Download className="h-4 w-4" />Descargar plantilla Excel
              </Button>
            </div>
            <div className="bg-muted/50 border rounded-lg p-3 text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">ℹ️ Columnas requeridas:</p>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Código:</strong> Identificador único (ej: AL001)</li>
                <li><strong>Tipo Sistema:</strong> {Object.keys(ALL_SYSTEM_TYPES).slice(0, 5).map(t => getSystemTypeLabel(t)).join(', ')}...</li>
                <li><strong>Severidad:</strong> baja, media, alta</li>
              </ul>
            </div>
            <div
              className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
              <p className="font-medium">Haz clic para seleccionar un archivo</p>
              <p className="text-sm text-muted-foreground mt-1">Formatos: .xlsx, .xls, .csv</p>
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFileChange} />
            </div>
          </div>
        )}

        {step === 'preview' && (
          <div className="space-y-4 flex-1 min-h-0">
            <div className="flex items-center gap-3 flex-wrap">
              {validRows.length > 0 && <Badge variant="default" className="gap-1"><CheckCircle className="h-3 w-3" />{validRows.length} válidos</Badge>}
              {invalidRows.length > 0 && <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" />{invalidRows.length} con errores</Badge>}
              <span className="text-sm text-muted-foreground">Total: {parsedRows.length} filas</span>
            </div>
            <ScrollArea className="h-[350px] border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Código</TableHead>
                    <TableHead>Tipo Sistema</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead>Severidad</TableHead>
                    <TableHead className="w-28">Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parsedRows.map((row) => (
                    <TableRow key={row.rowIndex} className={row.errors.length > 0 ? 'bg-destructive/10' : ''}>
                      <TableCell className="text-muted-foreground text-xs">{row.rowIndex}</TableCell>
                      <TableCell className="font-mono text-sm">{row.data.codigo || '—'}</TableCell>
                      <TableCell className="text-sm">{row.data.tipo_sistema || '—'}</TableCell>
                      <TableCell className="text-sm">{row.data.descripcion || '—'}</TableCell>
                      <TableCell className="text-sm">{row.data.severidad || '—'}</TableCell>
                      <TableCell>
                        {row.errors.length > 0 ? (
                          <span className="text-destructive text-xs flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />{row.errors[0]}
                          </span>
                        ) : (
                          <Badge variant="outline" className="text-xs gap-1 border-green-500 text-green-600">
                            <CheckCircle className="h-3 w-3" />OK
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
            <div className="flex justify-between items-center pt-2">
              <Button variant="outline" onClick={reset}>Cancelar</Button>
              <Button onClick={handleImport} disabled={validRows.length === 0} className="gap-2">
                <Upload className="h-4 w-4" />Importar {validRows.length} códigos
              </Button>
            </div>
          </div>
        )}

        {step === 'importing' && (
          <div className="py-8 space-y-4">
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" /><span className="font-medium">Importando códigos...</span>
            </div>
            <Progress value={progress} className="h-2" />
            <p className="text-center text-sm text-muted-foreground">{progress}%</p>
          </div>
        )}

        {step === 'done' && (
          <div className="py-8 space-y-4 text-center">
            <CheckCircle className="h-12 w-12 mx-auto text-green-500" />
            <h3 className="text-lg font-semibold">Importación completada</h3>
            <div className="flex justify-center gap-4 flex-wrap">
              {importResult.created > 0 && <Badge variant="default" className="text-sm gap-1"><CheckCircle className="h-3 w-3" />{importResult.created} creados</Badge>}
              {importResult.errors > 0 && <Badge variant="destructive" className="text-sm gap-1"><XCircle className="h-3 w-3" />{importResult.errors} con errores</Badge>}
            </div>
            <Button onClick={() => { handleOpenChange(false); onSuccess(); }} className="mt-4">Cerrar</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
